import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { RiFileTextLine, RiArrowDownSLine, RiUser3Line, RiTeamLine, RiArrowUpSLine } from 'react-icons/ri';
import { stripInlineReferencesForDisplay } from '../../utils/formatContent';
import { sanitizeMarkdownViz } from '../../utils/markdownComponents';
import InlineProgressCard from './ProgressDisplay';

// Stable plugin arrays so ReactMarkdown isn't re-parsed on a fresh array
// identity each render (mirror Response.js).
const SWARM_REMARK_PLUGINS = [remarkGfm];
const SWARM_REHYPE_PLUGINS = [rehypeSanitize];

// Split the raw streamed summary — which carries the model's literal
// "Summary:" / "Conclusive Response:" markers (the same ones the durable card
// parses out) — into display halves, so the live preview reads like the final
// card instead of showing the raw marker tokens.  Markers can straddle token
// deltas, so this operates on the full accumulated text and tolerates the
// "Conclusive Response:" marker not having arrived yet.
const splitStreamingSummary = (raw) => {
  const text = raw || '';
  const marker = 'Conclusive Response:';
  const idx = text.indexOf(marker);
  const stripLead = (s) => s.replace(/^\s*Summary:\s*/i, '').trimStart();
  if (idx === -1) {
    return { summaryPart: stripLead(text), conclusivePart: '' };
  }
  return {
    summaryPart: stripLead(text.slice(0, idx)).trimEnd(),
    conclusivePart: text.slice(idx + marker.length).trim(),
  };
};

// Memoized agent-message card, reused for committed messages AND the
// provisional live (streaming) card.  With stable props, committed cards
// skip re-parsing their markdown while a later turn token-streams.
const SwarmMessageCard = React.memo(function SwarmMessageCard({
  agentName,
  content,
  markdownComponentsNoViz,
}) {
  return (
    <div style={{ background: 'var(--panel-background, var(--input-background))', border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--border-color)', background: 'var(--input-background)' }}>
        <RiTeamLine size={15} />
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{agentName}</span>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div className="markdown-content" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' }}>
          <ReactMarkdown remarkPlugins={SWARM_REMARK_PLUGINS} rehypePlugins={SWARM_REHYPE_PLUGINS} components={markdownComponentsNoViz}>
            {content || '(no content)'}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
});

const SwarmResponse = ({
  terminalSummaryFallback,
  displaySummary,
  swarm,
  selectedSummaryIndex,
  setSelectedSummaryIndex,
  isReferencesExpanded,
  setIsReferencesExpanded,
  markdownComponents,
  markdownComponentsNoViz,
  summaryRef,
  swarmWorkflowSteps = [],
  isSwarmRunning = false,
  isSwarmTerminal = false,
}) => {
  const isActive = isSwarmRunning && !isSwarmTerminal;
  // In-progress turn partials (token deltas), excluding the summary track.
  const liveTurnDeltas = Object.values(swarm?.liveDeltas || {}).filter(
    (d) => d && d.speaker_id !== 'summary' && d.text
  );
  // Live streaming summary partial (speaker "summary", turn_index null →
  // key "null:summary").  Shown as a progressive preview until the durable
  // SUMMARY_GENERATED event lands and clears it (handed off to displaySummary).
  // NOT gated on isActive: the end-of-debate auto-summary streams AFTER the
  // session goes terminal, so it must render post-completion.  The hook clears
  // this partial on SUMMARY_GENERATED, summary error, stop, and reset, so a
  // non-empty partial always means "a summary is mid-stream right now".
  const liveSummaryText = swarm?.liveDeltas?.['null:summary']?.text || '';
  const showLiveSummary = liveSummaryText.length > 0;
  const liveSummary = showLiveSummary
    ? splitStreamingSummary(liveSummaryText)
    : null;
  return (
    <>
      {/* Debate events — always at the top */}
      {((swarm?.events && swarm.events.length > 0) ||
        (isActive && liveTurnDeltas.length > 0)) && (
        <div style={{ paddingTop: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {(swarm?.events || [])
              .filter(
                (evt) =>
                  evt.type === 'agent_message' ||
                  evt.type === 'user_intervention' ||
                  evt.type === 'human_initial_question' ||
                  evt.type === 'human_intervention_received'
              )
              .map((evt) => {
                const isHuman =
                  evt.type === 'human_initial_question' ||
                  evt.type === 'user_intervention' ||
                  evt.type === 'human_intervention_received';

                if (isHuman) {
                  const label = evt.type === 'human_initial_question' ? 'Initial Question' : 'Guidance';
                  const indicatorType = evt.type === 'human_initial_question' ? 'human' : 'intervention';
                  return (
                    <div key={evt.event_id} data-indicator={indicatorType} data-indicator-key={evt.event_id} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{ maxWidth: '80%' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--primary-color, #636cff)', textAlign: 'right', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <RiUser3Line size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          {label}
                        </div>
                        <div style={{ background: 'var(--primary-color, #636cff)', color: '#fff', borderRadius: '12px 12px 2px 12px', padding: '10px 14px', fontSize: '0.92rem', lineHeight: 1.5, wordBreak: 'break-word' }}>
                          {evt?.payload?.content || '(no content)'}
                        </div>
                      </div>
                    </div>
                  );
                }

                const speakerId = evt?.payload?.speaker_id;
                const agentName = swarm.participantDisplayNames?.[speakerId] || speakerId?.toUpperCase() || 'Agent';
                return (
                  <SwarmMessageCard
                    key={evt.event_id}
                    agentName={agentName}
                    content={evt?.payload?.content}
                    markdownComponentsNoViz={markdownComponentsNoViz}
                  />
                );
              })}
            {/* Provisional live card(s) for the in-progress turn — replaced by
                the durable agent_message card when the turn completes. */}
            {isActive && liveTurnDeltas.map((d) => {
              const agentName = swarm.participantDisplayNames?.[d.speaker_id] || d.speaker_id?.toUpperCase() || 'Agent';
              return (
                <SwarmMessageCard
                  key={`delta-${d.turn_index}:${d.speaker_id}`}
                  agentName={agentName}
                  content={d.text}
                  markdownComponentsNoViz={markdownComponentsNoViz}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Swarm session progress — last visible thing while the debate is running */}
      {isActive && swarmWorkflowSteps.length > 0 && (
        <InlineProgressCard steps={swarmWorkflowSteps} title="Swarm Flow" />
      )}

      {/* Error inline after events */}
      {swarm?.error && (
        <div
          style={{
            marginTop: '1rem',
            padding: '12px',
            backgroundColor: 'var(--color-danger-bg, #f8d7da)',
            border: '1px solid var(--color-danger-border, #f5c6cb)',
            borderRadius: '6px',
            color: 'var(--color-danger-text, #721c24)',
          }}
        >
          <strong>Error:</strong> {swarm.error}
        </div>
      )}

      {/* Live streaming summary preview — a progressive reveal of the summary
          as it token-streams, replaced by the durable parsed summary below
          once SUMMARY_GENERATED lands and clears the partial.  Dashed border
          distinguishes the in-progress preview from the final (solid) card. */}
      {showLiveSummary && (
        <div
          data-indicator="summary-streaming"
          data-indicator-key="summary-streaming"
          style={{
            marginTop: '1rem',
            padding: '16px',
            backgroundColor: 'var(--hover-color, rgba(0, 123, 255, 0.05))',
            border: '1px dashed var(--primary-color, #636cff)',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              color: 'var(--primary-color, #636cff)',
              fontWeight: 'bold',
            }}
          >
            <RiFileTextLine size={20} />
            <span>Summary (streaming…)</span>
          </div>
          <div style={{ marginBottom: liveSummary?.conclusivePart ? '16px' : 0 }}>
            <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>Summary:</div>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={SWARM_REMARK_PLUGINS} rehypePlugins={SWARM_REHYPE_PLUGINS} components={markdownComponents}>
                {sanitizeMarkdownViz(liveSummary?.summaryPart || '')}
              </ReactMarkdown>
            </div>
          </div>
          {liveSummary?.conclusivePart && (
            <div>
              <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>Conclusive Response:</div>
              <div className="markdown-content">
                <ReactMarkdown remarkPlugins={SWARM_REMARK_PLUGINS} rehypePlugins={SWARM_REHYPE_PLUGINS} components={markdownComponents}>
                  {sanitizeMarkdownViz(liveSummary.conclusivePart)}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Durable summary — shown once SUMMARY_GENERATED lands. Hidden while a
          fresh summary is streaming (showLiveSummary) so the stale prior
          summary and the live preview never render at once. */}
      {displaySummary && !showLiveSummary && (
        <div
          ref={summaryRef}
          data-indicator="summary"
          data-indicator-key="summary"
          style={{
            marginTop: '1rem',
            padding: '16px',
            backgroundColor: 'var(--hover-color, rgba(0, 123, 255, 0.05))',
            border: '1px solid var(--primary-color, #636cff)',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              marginBottom: '12px',
              color: 'var(--primary-color, #636cff)',
              fontWeight: 'bold',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RiFileTextLine size={20} />
              <span>Summary {displaySummary.is_final ? '(Final)' : `(Turn ${displaySummary.final_turn})`}</span>
            </div>

            {swarm?.summaries?.length > 1 && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <select
                  value={selectedSummaryIndex !== null ? selectedSummaryIndex : swarm.summaries.length - 1}
                  onChange={(e) => setSelectedSummaryIndex(parseInt(e.target.value, 10))}
                  style={{
                    padding: '4px 28px 4px 8px',
                    borderRadius: '4px',
                    border: '1px solid var(--vega-bindings-input-border, #d1d5db)',
                    background: 'var(--vega-bindings-input-bg, #fff)',
                    color: 'var(--vega-bindings-text-color, #1f2937)',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    outline: 'none',
                    maxWidth: '200px',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                  }}
                >
                  {swarm.summaries.map((s, idx) => (
                    <option key={idx} value={idx}>
                      {s.is_final ? 'Final' : `Turn ${s.final_turn}`}
                    </option>
                  ))}
                </select>
                <RiArrowDownSLine
                  size={18}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: 'var(--primary-color, #636cff)',
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>Summary:</div>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={SWARM_REMARK_PLUGINS} rehypePlugins={SWARM_REHYPE_PLUGINS} components={markdownComponents}>
                {sanitizeMarkdownViz(displaySummary.summary)}
              </ReactMarkdown>
            </div>
          </div>

          <div>
            <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>Conclusive Response:</div>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={SWARM_REMARK_PLUGINS} rehypePlugins={SWARM_REHYPE_PLUGINS} components={markdownComponents}>
                {sanitizeMarkdownViz(stripInlineReferencesForDisplay(displaySummary.conclusive_response))}
              </ReactMarkdown>
            </div>
          </div>

          {Array.isArray(displaySummary.references) && displaySummary.references.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setIsReferencesExpanded((prev) => !prev)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--primary-color, #636cff)',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  marginBottom: isReferencesExpanded ? '6px' : '0',
                }}
                aria-expanded={isReferencesExpanded}
              >
                {isReferencesExpanded ? <RiArrowUpSLine size={18} /> : <RiArrowDownSLine size={18} />}
                {`References (${displaySummary.references.length})`}
              </button>
              {isReferencesExpanded && (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {displaySummary.references.map((refUrl, idx) => (
                    <li key={`${refUrl}-${idx}`} style={{ marginBottom: '4px', wordBreak: 'break-all' }}>
                      <a href={refUrl} target="_blank" rel="noopener noreferrer">
                        {refUrl}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Terminal fallback — shown at the bottom when no summary exists yet */}
      {terminalSummaryFallback && (
        <div
          style={{
            marginTop: '1rem',
            padding: '12px 14px',
            backgroundColor: 'var(--bg-secondary, #f5f5f5)',
            border: '1px solid var(--border-color, #ddd)',
            borderRadius: '8px',
            color: 'var(--text-secondary, #666)',
            fontSize: '0.92rem',
          }}
        >
          {terminalSummaryFallback}
        </div>
      )}
    </>
  );
};

export default SwarmResponse;
